import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useContext, useMemo } from "react";
import { DialogProvider, createDialogContext } from "../../../Common/Dialog";
import { KYDTicketType } from "@common/interfaces/KYDTicket";

export const DeleteTicketTypeDialogContext = createDialogContext();

const DeleteTicketTypeDialog = ({
  onDelete,
  onClose,
  ticketType,
  isLoading = false,
}: {
  ticketType: KYDTicketType;
  isLoading: boolean;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
}) => {
  const { isOpen } = useContext(DeleteTicketTypeDialogContext);

  const _onClose = () => {
    onClose();
  };

  const hasTiers = useMemo(
    () => ticketType?.pool?.ticket_types.length > 1,
    [ticketType?.pool?.ticket_types]
  );

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={_onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">Delete ticket type {ticketType?.name}</Text>
            </VStack>
          </ModalHeader>
          <ModalBody textAlign={"left"}>
            <VStack w="full">
              <Text w="full">
                Are you sure you want to delete this ticket type?
              </Text>
              {hasTiers && <Text w="full">All the tiers will be deleted</Text>}
              <Text color="red.500" w="full">
                This action can not be undone.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"delete"}
                bg="red.500"
                color="white"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading}
                onClick={() => onDelete(ticketType?.id)}
              >
                Delete ticket type
              </Button>
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading}
                w={"auto"}
                onClick={_onClose}
              >
                Cancel
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withDeleteTicketTypeDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={DeleteTicketTypeDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default DeleteTicketTypeDialog;
